import React from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Header } from "./components/Header";
import { Home } from "./pages/Home";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { CVBuilder } from "./pages/CVBuilder";
import { CVList } from "./pages/CVList";
import { Backoffice } from "./pages/Backoffice";
import { Profile } from "./pages/Profile";
import Dashboard from "./pages/Dashboard";
import AdminTemplate from "./pages/AdminTemplate";
import { Toaster } from "./components/ui/sonner";

// GLOBAL 401 handler — Feb 2026: applies to ALL axios calls (including per-page
// `import axios from 'axios'` in CVBuilder/Backoffice/etc). When the backend
// returns 401, we clear stored credentials and hard-redirect to /login instead
// of leaving the user stuck on a "Token expirado" toast.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      try {
        localStorage.removeItem('evolutt_token');
        localStorage.removeItem('evolutt_user');
      } catch (_) {}
      const url = (error.config && error.config.url) || '';
      const path = (typeof window !== 'undefined' && window.location.pathname) || '';
      if (!url.includes('/auth/login') && !path.startsWith('/login')) {
        window.location.assign('/login?expired=1');
      }
    }
    return Promise.reject(error);
  }
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// "Início" navigation target:
// Both anonymous AND authenticated users land on the public Home page (the
// landing/marketing page). Authenticated users continue to have full nav access
// (My CVs, Backoffice, etc.) via the header — Início is just a "back to home"
// link for everyone, consistent with most web apps.
const HomeRedirect = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return <Layout><Home /></Layout>;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!user || !isAdmin()) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Layout component with header
const Layout = ({ children, showHeader = true }) => {
  return (
    <div className="app-container">
      {showHeader && <Header />}
      <main className="flex-1">{children}</main>
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route 
        path="/cv/new" 
        element={
          <ProtectedRoute>
            <Layout><CVBuilder /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/cvs" 
        element={
          <ProtectedRoute>
            <Layout><CVList /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/backoffice" 
        element={
          <AdminRoute>
            <Backoffice />
          </AdminRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <AdminRoute>
            <Layout><Dashboard /></Layout>
          </AdminRoute>
        } 
      />
      <Route
        path="/admin/template"
        element={
          <AdminRoute>
            <Layout><AdminTemplate /></Layout>
          </AdminRoute>
        }
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Layout><Profile /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
